import type { DashboardRookieMemberDto } from "@/modules/boxDashboard/gateway/boxDashboardApiDto"
import dayjs from "dayjs"

const createRookieMemberDto = (
  id: string,
  memberId: string,
  firstname: string,
  lastname: string,
  nbOfDaysSinceRegistration: number,
  totalSessions: number,
  lastSessionDate: string | null,
  nextSessionDate: string | null,
  purchaseName: string,
  avatarUrl: string | null = null,
): DashboardRookieMemberDto => ({
  id,
  totalSessions,
  nbOfDaysSinceRegistration,
  lastSessionDate,
  nextSessionDate,
  member: {
    id: memberId,
    firstname,
    lastname,
    status: "ACTIVE",
    avatar: avatarUrl ? { url: avatarUrl, hash: "hash", key: "key" } : null,
    username: `${firstname.toLowerCase()}${lastname.toLowerCase()}`,
    note: null,
    lastVisited: null,
    noShowPastMonth: 0,
    activeOffers: [],
    archivedDate: null,
    inscriptionDate: dayjs().subtract(nbOfDaysSinceRegistration, "days").toISOString(),
    archived: false,
    lastMembership: null,
  },
  purchaseName,
  archived: false,
  archivedDate: null,
  createdDate: dayjs().subtract(nbOfDaysSinceRegistration, "days").toISOString(),
})

export const ROOKIE_MEMBER_DEFAULT: DashboardRookieMemberDto = createRookieMemberDto(
  "rookie-1",
  "member-rookie-1",
  "Alex",
  "Thompson",
  10,
  5,
  dayjs().subtract(2, "days").toISOString(),
  dayjs().add(1, "day").toISOString(),
  "Monthly Unlimited",
)

export const ROOKIE_MEMBERS_LIST: DashboardRookieMemberDto[] = [
  // Under 15 days - Brand new rookies
  ROOKIE_MEMBER_DEFAULT,
  createRookieMemberDto(
    "rookie-2",
    "member-rookie-2",
    "Jordan",
    "Rivera",
    5,
    3,
    dayjs().subtract(1, "day").toISOString(),
    dayjs().add(2, "days").toISOString(),
    "10 Session Pack",
  ),
  createRookieMemberDto(
    "rookie-3",
    "member-rookie-3",
    "Quinn",
    "Davis",
    8,
    2,
    dayjs().subtract(5, "days").toISOString(),
    dayjs().add(2, "days").toISOString(),
    "5 Session Pack",
  ),
  createRookieMemberDto(
    "rookie-4",
    "member-rookie-4",
    "Drew",
    "Martinez",
    12,
    6,
    dayjs().subtract(3, "days").toISOString(),
    dayjs().add(1, "day").toISOString(),
    "10 Session Pack",
  ),
  createRookieMemberDto(
    "rookie-4b",
    "member-rookie-4b",
    "Emma",
    "Wilson",
    3,
    2,
    dayjs().subtract(1, "day").toISOString(),
    dayjs().add(1, "day").toISOString(),
    "Monthly Unlimited",
  ),
  createRookieMemberDto(
    "rookie-4c",
    "member-rookie-4c",
    "Liam",
    "Johnson",
    7,
    4,
    dayjs().subtract(2, "days").toISOString(),
    dayjs().add(3, "days").toISOString(),
    "Annual Membership",
  ),
  createRookieMemberDto(
    "rookie-4d",
    "member-rookie-4d",
    "Olivia",
    "Brown",
    11,
    7,
    dayjs().subtract(1, "day").toISOString(),
    null,
    "10 Session Pack",
  ),
  createRookieMemberDto(
    "rookie-4e",
    "member-rookie-4e",
    "Noah",
    "Taylor",
    2,
    1,
    null,
    dayjs().add(1, "day").toISOString(),
    "5 Session Pack",
  ),
  createRookieMemberDto(
    "rookie-4f",
    "member-rookie-4f",
    "Ava",
    "Anderson",
    9,
    5,
    dayjs().subtract(3, "days").toISOString(),
    dayjs().add(2, "days").toISOString(),
    "Monthly Unlimited",
    "https://i.pravatar.cc/150?u=ava",
  ),
  createRookieMemberDto(
    "rookie-4g",
    "member-rookie-4g",
    "Sophia",
    "Thomas",
    6,
    3,
    dayjs().subtract(2, "days").toISOString(),
    dayjs().add(1, "day").toISOString(),
    "Monthly Unlimited",
  ),
  createRookieMemberDto(
    "rookie-4h",
    "member-rookie-4h",
    "Mason",
    "Jackson",
    13,
    8,
    dayjs().subtract(1, "day").toISOString(),
    dayjs().add(2, "days").toISOString(),
    "Annual Membership",
    "https://i.pravatar.cc/150?u=mason",
  ),
  createRookieMemberDto(
    "rookie-4i",
    "member-rookie-4i",
    "Isabella",
    "White",
    4,
    2,
    dayjs().subtract(1, "day").toISOString(),
    dayjs().add(1, "day").toISOString(),
    "10 Session Pack",
  ),
  createRookieMemberDto(
    "rookie-4j",
    "member-rookie-4j",
    "Ethan",
    "Harris",
    10,
    6,
    dayjs().subtract(2, "days").toISOString(),
    null,
    "Monthly Unlimited",
  ),
  createRookieMemberDto(
    "rookie-4k",
    "member-rookie-4k",
    "Mia",
    "Clark",
    1,
    0,
    null,
    dayjs().add(1, "day").toISOString(),
    "5 Session Pack",
  ),
  createRookieMemberDto(
    "rookie-4l",
    "member-rookie-4l",
    "James",
    "Lewis",
    14,
    9,
    dayjs().subtract(1, "day").toISOString(),
    dayjs().add(3, "days").toISOString(),
    "Monthly Unlimited",
  ),

  // Under 30 days - Getting into the routine
  createRookieMemberDto(
    "rookie-5",
    "member-rookie-5",
    "Casey",
    "Morgan",
    18,
    8,
    dayjs().subtract(3, "days").toISOString(),
    null,
    "Monthly Unlimited",
  ),
  createRookieMemberDto(
    "rookie-6",
    "member-rookie-6",
    "Skyler",
    "Patel",
    22,
    10,
    dayjs().subtract(1, "day").toISOString(),
    dayjs().add(1, "day").toISOString(),
    "Monthly Unlimited",
  ),
  createRookieMemberDto(
    "rookie-7",
    "member-rookie-7",
    "Taylor",
    "Brooks",
    25,
    12,
    dayjs().subtract(1, "day").toISOString(),
    dayjs().add(1, "day").toISOString(),
    "Annual Membership",
    "https://i.pravatar.cc/150?u=taylor",
  ),

  // Under 45 days - Building consistency
  createRookieMemberDto(
    "rookie-8",
    "member-rookie-8",
    "Morgan",
    "Lee",
    35,
    15,
    dayjs().subtract(2, "days").toISOString(),
    dayjs().add(3, "days").toISOString(),
    "Monthly Unlimited",
  ),
  createRookieMemberDto(
    "rookie-9",
    "member-rookie-9",
    "Reese",
    "Garcia",
    38,
    14,
    dayjs().subtract(1, "day").toISOString(),
    dayjs().add(2, "days").toISOString(),
    "Monthly Unlimited",
    "https://i.pravatar.cc/150?u=reese",
  ),
  createRookieMemberDto(
    "rookie-10",
    "member-rookie-10",
    "Riley",
    "Chen",
    42,
    20,
    dayjs().subtract(1, "day").toISOString(),
    dayjs().add(1, "day").toISOString(),
    "20 Session Pack",
  ),

  // Under 60 days - Almost graduated
  createRookieMemberDto(
    "rookie-11",
    "member-rookie-11",
    "Avery",
    "Kim",
    50,
    25,
    dayjs().subtract(4, "days").toISOString(),
    null,
    "Annual Membership",
    "https://i.pravatar.cc/150?u=avery",
  ),
  createRookieMemberDto(
    "rookie-12",
    "member-rookie-12",
    "Jamie",
    "Wilson",
    55,
    18,
    dayjs().subtract(2, "days").toISOString(),
    dayjs().add(4, "days").toISOString(),
    "Annual Membership",
  ),
]
